import { Delete } from '@mui/icons-material';
import { Button, Paper } from '@mui/material';

import styles from './RetouchCaseDetail.module.css';

import { Article, VideoCase } from '../../../api/caseApiTypes';
import { Right } from '../../../api/userApi';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppRedux';
import { useRights } from '../../../hooks/useRights';
import { ImagesGridDetailsView } from '../../images/detailsView/ImagesGridDetailsView';
import { CaseComments } from '../CaseComments';
import { CaseMedia } from '../CaseMedia';
import { CaseStateProgress } from '../CaseStateProgress';
import { CaseTags } from '../CaseTags';
import { CaseTypeDetailComponentProps } from '../CaseTypeDetail';
import { upsertArticles, removeArticle, uploadVideo } from '../caseDetailSlice';
import { CaseDeadlineDates } from '../formFieldComponents/CaseDeadlineDates';
import { CaseExpansion } from '../layoutComponents/CaseExpansion';
import { CaseFormBlock } from '../layoutComponents/CaseFormBlock';
import { CaseFormButtonContainer } from '../layoutComponents/CaseFormButtonContainer';
import { CaseFormContent } from '../layoutComponents/CaseFormContent';
import { CaseFormSidebar } from '../layoutComponents/CaseFormSidebar';
import { FormFieldsWrapper } from './FormFieldsWrapper';
import { CaseFormSection } from './CaseFormSection';
import { ShowDeliverySections } from './CaseFormLogic';
import { CaseProceedStateButton } from '../CaseProceedStateButton';
import { VideoCaseDetailFormFields } from './VideoCaseDetailFormFields';
import { isAnyMediaSelected, resetSelectedContext } from '../../images/imagesSlice';
import { useSelectionContext } from '../../images/selectedImagesContext/selectionContextContext';

type TCase = VideoCase;

interface Props extends CaseTypeDetailComponentProps {
  caze: TCase;
}

export const VideoCaseDetail: React.FC<Props> = ({ caze, isNewCase, readonly, onDeleteCase, onDeleteAttachment, onCasePartialChange }) => {
  const isAdmin = useRights(Right.Administration);
  const dispatch = useAppDispatch();

  const onUpsertArticle = (article: Article) => {
    dispatch(upsertArticles({ item: caze, articles: [article] }));
  };

  const onUpsertManyArticles = (articles: Article[]) => {
    dispatch(upsertArticles({ item: caze, articles }));
  };

  const onDeleteArticle = ({ id }: Article) => {
    if (id != null) {
      dispatch(removeArticle({ item: caze, id: id }));
    }
  };

  const selectionContext = useSelectionContext();
  const anySelected = useAppSelector(isAnyMediaSelected(selectionContext));
  const clearSelection = () => dispatch(resetSelectedContext(selectionContext));

  const canRemoveCase = isAdmin || !readonly;

  return (
    <>
      <CaseFormContent>
        <FormFieldsWrapper caze={caze} onChange={onCasePartialChange} isNewCase={isNewCase}>
          <VideoCaseDetailFormFields
            caze={caze}
            isNewCase={isNewCase}
            readonly={readonly}
            onChange={onCasePartialChange}
            onUpsertArticle={onUpsertArticle}
            onUpsertManyArticles={onUpsertManyArticles}
            onDeleteArticle={onDeleteArticle}
            onDeleteAttachment={onDeleteAttachment}
          />
        </FormFieldsWrapper>
        {ShowDeliverySections(caze) && (
          <CaseFormSection title="Filmklipp" caseId={caze.id}>
            <CaseMedia caze={caze} mediaPointers={caze.videoPointers} caseReadonly={readonly} uploadMediaAction={uploadVideo} />
          </CaseFormSection>
        )}
        <CaseProceedStateButton caze={caze} isNew={isNewCase} />
      </CaseFormContent>
      <CaseFormSidebar>
        <CaseFormBlock>
          <CaseStateProgress caze={caze} isNewCase={isNewCase} />

          <CaseDeadlineDates caze={caze} onChange={onCasePartialChange} readonly={readonly} />

          <CaseTags caze={caze} readonly={isNewCase || readonly} size="small" />

          {canRemoveCase && (
            <CaseFormButtonContainer renderDivider>
              <Button variant="outlined" color="secondary" startIcon={<Delete />} onClick={onDeleteCase} disabled={isNewCase}>
                Ta Bort Beställningen
              </Button>
            </CaseFormButtonContainer>
          )}
        </CaseFormBlock>

        {anySelected && (
          <Paper className={styles.imageInformation}>
            <ImagesGridDetailsView onClose={clearSelection} />
          </Paper>
        )}
        <CaseFormBlock>
          <CaseExpansion title="Kommentarer" hidden={isNewCase} compact>
            <CaseComments caze={caze} />
          </CaseExpansion>
        </CaseFormBlock>
      </CaseFormSidebar>
    </>
  );
};
